import type { FC } from 'react';
import React, { memo, useEffect, Fragment, useMemo } from 'react';

import { IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { IN_EDITOR_TEMPLATES_SEGMENT_KEY } from '@confluence/browser-metrics/entry-points/metric-keys.ts';

import { usePanelOpenAnalyticsEvents } from './usePanelOpenAnalyticsEvents.ts';
import { useLoadTemplatePanelExperienceTracker } from './useLoadTemplatePanelExperienceTracker.ts';
import { ContentCreationPanelTabs } from './ContentCreationPanelTabs.tsx';
import { collectLoadPanelExperiences, filterSubExperiences } from './experienceTrackerHelpers.ts';

type Props = {
	hasUserAddedContent: boolean | null;
	isPanelDisabled: boolean;
	title: string;
	onContentUpdated: () => void;
	beforeTemplateSelect?: () => void;
	beforeUpdateRequest?: () => void;
	isContentCreationPanelShown: boolean;
	onClickCloseContentCreationPanel: () => void;
	onError?: () => void;
	templateAllowList?: string[];
	isImportDisabled?: boolean;
	isAlwaysDiscardChanges?: boolean;
	isSpacePickerDisabled?: boolean;
};

export const EditorContextPanelContentCreationPanel: FC<Props> = memo(
	({
		hasUserAddedContent,
		isPanelDisabled,
		title,
		onContentUpdated,
		beforeTemplateSelect,
		isContentCreationPanelShown,
		onClickCloseContentCreationPanel,
		beforeUpdateRequest,
		onError,
		templateAllowList,
		isImportDisabled,
		isAlwaysDiscardChanges,
		isSpacePickerDisabled,
	}) => {
		const [currentSpaceKey] = usePageSpaceKey();
		const [contentId] = usePageContentId();
		const spaceId = useSpaceId();
		usePanelOpenAnalyticsEvents(isContentCreationPanelShown, contentId);
		const { abortTemplatePanelLoadExperiences } = useLoadTemplatePanelExperienceTracker();

		const subExperiences = useMemo(
			() => filterSubExperiences(isSpacePickerDisabled),
			[isSpacePickerDisabled],
		);

		useEffect(() => {
			if (!isContentCreationPanelShown) {
				abortTemplatePanelLoadExperiences('closing template panel');
			}
		}, [isContentCreationPanelShown, abortTemplatePanelLoadExperiences]);
		if (isContentCreationPanelShown && currentSpaceKey && contentId) {
			return (
				<Fragment>
					<ExperienceStart
						name={IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE}
						collect={collectLoadPanelExperiences(subExperiences)}
						timeout={ExperienceTimeout.IN_EDITOR_TEMPLATE_PANEL_LOAD}
					/>
					{subExperiences.map((name) => (
						<ExperienceStart name={name} key={name} />
					))}
					<PageSegmentLoadStart metricKey={IN_EDITOR_TEMPLATES_SEGMENT_KEY} />
					<ContentCreationPanelTabs
						currentSpaceKey={currentSpaceKey!}
						contentId={contentId!}
						spaceId={spaceId ?? ''}
						title={title}
						onContentUpdated={onContentUpdated}
						isPanelDisabled={isPanelDisabled}
						beforeTemplateSelect={beforeTemplateSelect}
						hasUserAddedContent={hasUserAddedContent}
						beforeUpdateRequest={beforeUpdateRequest}
						onError={onError}
						closeConfigPanel={onClickCloseContentCreationPanel}
						abortTemplatePanelLoadExperiences={abortTemplatePanelLoadExperiences}
						templateAllowList={templateAllowList}
						isImportDisabled={isImportDisabled}
						isAlwaysDiscardChanges={isAlwaysDiscardChanges}
						isSpacePickerDisabled={isSpacePickerDisabled}
					/>
				</Fragment>
			);
		}

		return null;
	},
);
